import React, { useRef } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import type { radioSelect_issueFieldRadioSelectReadviewFull_RadioSelectReadView$key as RadioSelectFragment } from '@atlassian/jira-relay/src/__generated__/radioSelect_issueFieldRadioSelectReadviewFull_RadioSelectReadView.graphql';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { HoverPopoverWithContext } from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { RadioSelectReadViewProps } from './types.tsx';

/**
 * The RadioSelectReadView will show a read only view of RadioSelect field.
 * @param props [RadioSelectReadViewProps](./types.tsx)
 */
export const RadioSelectReadView = ({
	fragmentRef,
	maxLines,
	shouldRenderHoverPopover,
}: RadioSelectReadViewProps) => {
	const data = useFragment<RadioSelectFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment radioSelect_issueFieldRadioSelectReadviewFull_RadioSelectReadView on JiraRadioSelectField
			@argumentDefinitions(
				isSingleLineEventDelegatorEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-single-line-event-delegator-enabled.relayprovider"
				}
			) {
				id @include(if: $isSingleLineEventDelegatorEnabled)
				selectedOption {
					label: value
				}
			}
		`,
		fragmentRef,
	);
	const { selectedOption } = data;

	const contentRef = useRef(null);

	const hoverPopoverRefFactory =
		shouldRenderHoverPopover && fg('issue_table_single_line_row_height_event_delegator')
			? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

	if (!selectedOption?.label) {
		return <EmptyFieldText />;
	}

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory && fg('issue_table_single_line_row_height_event_delegator')) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: selectedOption?.label,
		});
	}
	if (shouldRenderHoverPopover) {
		return fg('issue_table_single_line_row_height_event_delegator') ? (
			<Text maxLines={maxLines} ref={hoverPopoverRef}>
				{selectedOption?.label}
			</Text>
		) : (
			<HoverPopoverWithContext content={selectedOption?.label} contentRef={contentRef}>
				{({ ref }) => (
					<Text maxLines={maxLines} ref={mergeRefs(ref, contentRef)}>
						{selectedOption?.label}
					</Text>
				)}
			</HoverPopoverWithContext>
		);
	}

	return (
		<Text maxLines={maxLines} ref={contentRef}>
			{selectedOption?.label}
		</Text>
	);
};
